/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable sonarjs/no-duplicate-string */
import { t, Trans } from '@lingui/macro';
import AddressFormatter from '@shopify/address';
import { Button } from '@we-make-websites/ui-lib';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { useContext } from 'use-context-selector';
import { storeLocale } from '@/root/constants';
import { CartContext } from 'components/cartContext/CartContext';
import { CustomerContext } from 'components/customerContext/CustomerContext';
import { FeatureFlagsContext } from 'components/featureFlagsProvider/FeatureFlagsContext';
import { useCustomerAddressesQuery } from 'lib/shopify-storefront/__generated__/CustomerAddressesQuery';
import { storefrontQueryDataSourceUncached } from 'lib/shopify-storefront/dataSources';
import { sendDataToGTM } from 'utils/gtm/gtm';

export const CheckoutButton = () => {
  const router = useRouter();
  const locale = storeLocale(router.locale || router.defaultLocale);
  const featureFlagsContext = useContext(FeatureFlagsContext);
  if (featureFlagsContext === undefined)
    throw new Error(
      'CheckoutButton Button must be used within a FeatureFlagsContext'
    );

  const { featureFlags } = featureFlagsContext;
  const { amazonCart } = featureFlags[locale];

  if (amazonCart) return <AmazonCheckout />;

  return <ShopifyCheckout />;
};

const ShopifyCheckout = () => {
  const cartContext = useContext(CartContext);

  const customerContext = useContext(CustomerContext);

  if (cartContext === undefined)
    throw new Error('ShopifyCheckout Button must be used within a CartContext');

  const { customer } = customerContext;
  const { cart, cartLoading } = cartContext;
  const checkoutUrl = cart?.checkoutUrl;
  const { customerAccessToken } = useContext(CustomerContext);
  const router = useRouter();
  const locale = storeLocale(router.locale);
  const lang = locale.split('-')[0];
  const customerPreferredLanguage =
    customer?.preferredLanguage?.toLocaleLowerCase() ?? lang;

  const cartItemCount = cart?.lines.length ?? 0;
  const cartHasItems = cartItemCount > 0;
  const [checkoutUrlWithLocale, setCheckoutUrlWithLocale] = useState<
    string | null
  >(null);

  useEffect(() => {
    const fetchCheckoutUrl = async () => {
      const getConsumerUrl = async () => {
        if (customerAccessToken) {
          if (!checkoutUrl) {
            return null;
          }
          const variables = { customerAccessToken, first: 1 };
          const addressData = await useCustomerAddressesQuery.fetcher(
            storefrontQueryDataSourceUncached(locale),
            variables
          )();
          const defaultAddress = addressData.customer?.defaultAddress;

          const addressFormatter = new AddressFormatter(locale);
          const countries = await addressFormatter.getCountries();
          const consumerCountry = countries.find(
            (c) =>
              c.name.toLowerCase() === defaultAddress?.country?.toLowerCase()
          );
          const customerDataQuery = new URL(checkoutUrl);

          const consumerData = {
            'checkout[email]': customer?.email,
            'checkout[shipping_address][first_name]': customer?.firstName,
            'checkout[shipping_address][last_name]': customer?.lastName,
            'checkout[shipping_address][country]': consumerCountry?.code,
            'checkout[shipping_address][address1]': defaultAddress?.address1,
            'checkout[shipping_address][address2]': defaultAddress?.address2,
            'checkout[shipping_address][city]': defaultAddress?.city,
            'checkout[shipping_address][zip]': defaultAddress?.zip,
            'checkout[shipping_address][phone]': defaultAddress?.phone,
          };

          Object.entries(consumerData).forEach(([key, value]) => {
            if (!!value && value.length > 0) {
              customerDataQuery.searchParams.set(key, value);
            }
          });
          return customerDataQuery.toString();
        }
        return null;
      };

      const consumerUrl = await getConsumerUrl();

      let url;
      if (consumerUrl && locale !== 'en-GB') {
        url = `${consumerUrl}&locale=${customerPreferredLanguage}`;
      } else {
        url = `${checkoutUrl}&locale=${customerPreferredLanguage}`;
      }

      setCheckoutUrlWithLocale(url);
    };

    fetchCheckoutUrl();
  }, [
    checkoutUrl,
    customerAccessToken,
    customerPreferredLanguage,
    locale,
    customer,
  ]);

  return (
    <Button
      to={checkoutUrlWithLocale ?? ''}
      href={checkoutUrlWithLocale ?? ''}
      type="button"
      modifiers="block"
      disabled={cartLoading || !checkoutUrl || !cartHasItems}
    >
      <Trans id="cart.summary.checkout">Checkout</Trans>
    </Button>
  );
};

const AmazonCheckout = () => {
  const cartContext = useContext(CartContext);
  const formRef = useRef<HTMLFormElement>(null);

  if (cartContext === undefined)
    throw new Error('AmazonCheckout Button must be used within a CartContext');

  const { cart, cartLoading } = cartContext;
  const lines = cart?.lines ?? [];
  const cartItemCount = lines.length ?? 0;
  const cartHasItems = cartItemCount > 0;

  const awsAccessKeyId = process.env.NEXT_PUBLIC_AWS_ACCESS_KEYID ?? '';
  const associateTag = process.env.NEXT_PUBLIC_ASSOCIATE_TAG ?? '';

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (!formRef.current) return;

    event.preventDefault();

    const orderConfirmationData = {
      event: 'orderConfirmation',
      ecommerce: {
        currencyCode: cart?.estimatedCost.totalAmount.currencyCode,
        purchase: {
          actionField: {
            affiliation: 'AMAZON.COM',
            revenue: cart?.estimatedCost.totalAmount.amount,
          },
          products: lines.map((line) => {
            const availableForSale = line.merchandise.availableForSale ?? false;
            const price = line.merchandise.amountPerQuantity ?? 0;

            return {
              name: line?.merchandise.product.title,
              id: line?.merchandise.metafields?.sku ?? '',
              price,
              brand: 'OPI Products',
              category: line?.merchandise.metafields?.systemName,
              variant: line?.merchandise.title,
              quantity: line.quantity,
              dimension2: line?.merchandise.metafields?.primaryColorFamily,
              dimension6: line?.merchandise.metafields?.secondaryColorFamily,
              dimension7: line?.merchandise.metafields?.colorFamilySubgroup,
              dimension8: line?.merchandise.metafields?.finish,
              dimension9: line?.merchandise.metafields?.depthOfColor,
              dimension10: line?.merchandise.metafields?.cadColorOpacity,
              dimension12: line?.merchandise.metafields?.hexColor,
              dimension13: line?.merchandise.product.title,
              dimension14: line?.merchandise.metafields?.systemName,
              dimension3: availableForSale ? 'IN STOCK' : 'OUT OF STOCK',
            };
          }),
        },
      },
    };

    sendDataToGTM(orderConfirmationData);
    formRef.current.submit();
  };

  return (
    <form
      id="form-amazon-submit"
      action="//amazon.com/gp/aws/cart/add.html"
      target="_blank"
      onSubmit={onSubmit}
      ref={formRef}
      aria-label={t({
        id: 'checkout.amazonButton',
        message: 'amazon submit button, opens a new tab',
      })}
    >
      <input type="hidden" name="AWSAccessKeyId" value={awsAccessKeyId} />
      <input type="hidden" name="AssociateTag" value={associateTag} />

      {lines.map((cartLine, index) => {
        return (
          <React.Fragment key={index}>
            <input
              type="hidden"
              name={`ASIN.${index + 1}`}
              value={cartLine.merchandise.metafields?.asin ?? ''}
            />
            <input
              type="hidden"
              name={`Quantity.${index + 1}`}
              value={cartLine.quantity}
            />
          </React.Fragment>
        );
      })}

      <fieldset disabled={cartLoading}>
        <legend className="visually-hidden">
          <Trans id="cart.summary.amazonLegend">Amazon Checkout</Trans>
        </legend>
        <Button modifiers="block" disabled={cartLoading || !cartHasItems}>
          <Trans id="cart.summary.amazon">checkout on AMAZON.COM</Trans>
        </Button>
      </fieldset>
    </form>
  );
};
